import axios, { AxiosInstance, AxiosResponse, AxiosRequestConfig, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { 
  User, 
  Project, 
  ProjectCarton,
  ProjectPanel, 
  ProjectHardware,
  ProjectCartonPanel,
  LoginCredentials, 
  AuthResponse, 
  ApiResponse, 
  PaginatedResponse,
  CartonFilter,
  ProjectFilter,
  DashboardStats,
  Notification,
  CartonReceivingRequest,
  CartonPanelReceivingRequest,
  CartonHardwareReceivingRequest,
  CartonReceivingStatus,
  Report,
  SyncStatus
} from '../types';
import { cacheService } from './cacheService';
import { API_BASE_URL } from '../config/api';
import { logActivity } from '../utils/activityLogger';

interface ApiError extends Error {
  status?: number;
  data?: any;
}

interface LaravelErrorResponse {
  message: string;
  errors?: { [key: string]: string[] };
  status?: number;
}

const createApiError = (message: string, status?: number, data?: any): ApiError => {
  const error = new Error(message) as ApiError;
  error.status = status;
  error.data = data;
  return error;
};

export class ApiService {
  private api: AxiosInstance;
  private baseUrl: string;
  private currentUser: User | null = null;

  constructor() {
    // Use relative path for development (proxy will handle it) or full URL for production
    this.baseUrl = process.env.NODE_ENV === 'production' 
      ? (process.env.REACT_APP_API_URL || '/api')
      : '/api';
    this.api = axios.create({
      baseURL: this.baseUrl,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
    });

    this.setupInterceptors();
    this.initializeFromCache();
    
    // Make test functions available in development
    if (process.env.NODE_ENV === 'development') {
      (window as any).testLoginFormats = (identifier: string, pin: string) => {
        this.testLoginFormats(identifier, pin);
      };
      
      (window as any).testMinimalLogin = async () => {
        console.log('🧪 Testing minimal login with basic data...');
        try {
          const response = await this.api.post('/auth/login', {
            identifier: 'test',
            pin: this.encodeBase64('123456'),
            method: 'pin'
          });
          console.log('✅ Minimal login successful:', response.data);
        } catch (error) {
          console.error('❌ Minimal login failed:', error);
        }
      };
      
      (window as any).checkApiConnection = async () => {
        console.log('🔗 Testing API connection...');
        try {
          const response = await this.api.get('/health');
          console.log('✅ API connection successful:', response.data);
        } catch (error) {
          console.error('❌ API connection failed:', error);
        }
      };
      
      (window as any).checkSession = () => {
        console.log('🔍 Session Status:', {
          hasToken: !!localStorage.getItem('auth_token'),
          hasUser: !!this.currentUser,
          tokenValue: localStorage.getItem('auth_token')?.substring(0, 20) + '...',
          userData: this.currentUser,
          isLoggedIn: this.isLoggedIn()
        });
      };
      
      (window as any).clearSession = () => {
        console.log('🧹 Manually clearing session...');
        this.clearAuthData();
        console.log('✅ Session cleared');
      };
      
      (window as any).validateSession = async () => {
        console.log('🔑 Validating session...');
        const isValid = await this.validateToken();
        console.log('Session valid:', isValid);
        return isValid;
      };
      
      (window as any).testBasicLogin = async () => {
        console.log('🧪 Testing basic login with minimal data...');
        try {
          const response = await this.api.post('/auth/login', {
            identifier: 'test',
            pin: this.encodeBase64('123456'),
            method: 'pin'
          });
          console.log('✅ Basic login successful:', response.data);
          return response.data;
        } catch (error: any) {
          console.error('❌ Basic login failed:', error);
          console.error('Error details:', {
            status: error?.response?.status,
            message: error?.response?.data?.message,
            data: error?.response?.data
          });
          return null;
        }
      };
      
      (window as any).testRawLogin = async (identifier: string, pin: string) => {
        console.log('🧪 Testing raw login with provided data...');
        console.log('Input:', { identifier, pin });
        
        try {
          const response = await this.api.post('/auth/login', {
            identifier: identifier,
            pin: this.encodeBase64(pin),
            method: 'pin'
          });
          console.log('✅ Raw login successful:', response.data);
          return response.data;
        } catch (error: any) {
          console.error('❌ Raw login failed:', error);
          console.error('Error details:', {
            status: error?.response?.status,
            message: error?.response?.data?.message,
            data: error?.response?.data,
            requestData: { identifier, pin: this.encodeBase64(pin) }
          });
          return null;
        }
      };
      
      (window as any).testBackendHealth = async () => {
        console.log('🏥 Testing backend health...');
        try {
          // Try a simple GET request first
          const healthResponse = await this.api.get('/health');
          console.log('✅ Backend health check successful:', healthResponse.data);
          return true;
        } catch (error: any) {
          console.log('⚠️ Health check failed, trying alternative endpoints...');
          
          try {
            // Try the root endpoint
            const rootResponse = await this.api.get('/');
            console.log('✅ Root endpoint accessible:', rootResponse.status);
            return true;
          } catch (rootError: any) {
            console.error('❌ Backend appears to be down:', {
              healthError: error?.response?.status,
              rootError: rootError?.response?.status,
              message: error?.message || rootError?.message
            });
            return false;
          }
        }
      };
      
      (window as any).bypassLogin = async () => {
        console.log('🚀 Bypassing login for testing...');
        try {
          // Create a mock user for testing
          const mockUser = {
            id: 1,
            name: 'Test User',
            email: 'test@example.com',
            role: 'admin'
          };
          
          const mockToken = 'mock-token-' + Date.now();
          
          // Store mock auth data
          localStorage.setItem('auth_token', mockToken);
          localStorage.setItem('user_data', JSON.stringify(mockUser));
          
          // Set current user
          this.currentUser = mockUser;
          
          console.log('✅ Mock login successful:', { user: mockUser, token: mockToken });
          return { user: mockUser, token: mockToken };
        } catch (error) {
          console.error('❌ Mock login failed:', error);
          return null;
        }
      };
      
      (window as any).debugSession = () => {
        console.log('🔍 Session Debug Info:', {
          hasToken: !!localStorage.getItem('auth_token'),
          tokenValue: localStorage.getItem('auth_token')?.substring(0, 20) + '...',
          hasUserData: !!localStorage.getItem('user_data'),
          userData: localStorage.getItem('user_data'),
          currentUser: this.currentUser,
          isLoggedIn: this.isLoggedIn(),
          timestamp: new Date().toISOString()
        });
      };
      
      (window as any).testTokenValidation = async () => {
        console.log('🧪 Testing token validation...');
        try {
          const result = await this.validateToken();
          console.log('Token validation result:', result);
          return result;
        } catch (error) {
          console.error('Token validation error:', error);
          return false;
        }
      };
      
      (window as any).testSessionPersistence = async () => {
        console.log('🧪 Testing session persistence...');
        const token = localStorage.getItem('auth_token');
        const userData = localStorage.getItem('user_data');
        
        console.log('Session data:', {
          hasToken: !!token,
          tokenLength: token?.length || 0,
          hasUserData: !!userData,
          userData: userData ? JSON.parse(userData) : null
        });
        
        if (!token || !userData) {
          console.log('❌ No session data found');
          return false;
        }
        
        try {
          const isValid = await this.validateToken();
          console.log('✅ Token validation result:', isValid);
          
          if (isValid) {
            const user = await this.checkAuth();
            console.log('✅ User data retrieved:', user);
            return true;
          } else {
            console.log('❌ Token validation failed');
            return false;
          }
        } catch (error) {
          console.error('❌ Session persistence test failed:', error);
          return false;
        }
      };
    }
  }

  private async initializeFromCache(): Promise<void> {
    try {
      const cachedAuth = await cacheService.getCachedAuth();
      if (cachedAuth) {
        this.currentUser = cachedAuth.user;
        localStorage.setItem('auth_token', cachedAuth.token);
        localStorage.setItem('user_data', JSON.stringify(cachedAuth.user));
        
        // Set up token refresh interval
        this.setupTokenRefresh();
      }
    } catch (error) {
      console.warn('Failed to initialize from cache:', error);
    }
  }

  setupTokenRefresh(): void {
    // Refresh token every 30 minutes to prevent expiration
    setInterval(async () => {
      if (this.currentUser && navigator.onLine) {
        try {
          // Make a simple API call to refresh the token
          await this.api.post('/auth/me');
          console.log('🔄 Token refreshed successfully');
        } catch (error: any) {
          console.warn('⚠️ Token refresh failed:', error);
          
          // If it's a 401 error, the token is definitely expired
          if (error?.response?.status === 401) {
            console.log('🔑 Token refresh returned 401, clearing auth data');
            this.clearAuthData();
          }
        }
      }
    }, 30 * 60 * 1000); // 30 minutes
  }

  private setupInterceptors() {
    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      async (config: InternalAxiosRequestConfig) => {
        const token = localStorage.getItem('auth_token');
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error: AxiosError) => Promise.reject(this.handleError(error))
    );

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      async (error: AxiosError) => {
        if (error.response?.status === 401) {
          // Check if we're offline - don't logout if offline
          const isOffline = !navigator.onLine;
          if (isOffline) {
            console.log('🌐 Offline mode detected, not logging out on 401 error');
            return Promise.reject(this.handleError(error));
          }
          
          // Check if this is a sync operation - don't logout during sync
          const isSyncOperation = error.config?.url?.includes('/sync') || 
                                 error.config?.url?.includes('/receive') ||
                                 error.config?.url?.includes('/carton');
          
          if (isSyncOperation) {
            console.log('🔄 Sync operation detected, not logging out on 401 error');
            return Promise.reject(this.handleError(error));
          }
          
          // Check if this is a token validation call - handle it specially
          const isTokenValidation = error.config?.url?.includes('/auth/validate') || 
                                   error.config?.url?.includes('/auth/me');
          
          if (isTokenValidation) {
            console.log('🔑 Token validation failed, will be handled by AuthContext');
            return Promise.reject(this.handleError(error));
          }
          
          // Only logout for actual authentication failures on protected routes
          console.log('🔐 Authentication failed on protected route, logging out user');
          await cacheService.clearAuth();
          this.logout();
          window.location.href = '/login';
        }
        return Promise.reject(this.handleError(error));
      }
    );
  }

  private handleError(error: AxiosError): ApiError {
    const isDevelopment = process.env.NODE_ENV === 'development';

    // Handle network errors
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK' || error.response?.status === 503) {
      return createApiError(
        isDevelopment 
          ? `Server Unavailable: ${error.message}`
          : 'The server is currently unavailable. Please try again in a few moments.',
        503
      );
    }

    if (error.response) {
      const errorData = error.response.data as LaravelErrorResponse;
      const status = error.response.status;

      // Handle Laravel API error format
      if (errorData?.message) {
        // Check for specific PHP regex errors
        if (errorData.message.includes('preg_match') || errorData.message.includes('Delimiter must not be alphanumeric')) {
          console.error('PHP regex error detected:', {
            message: errorData.message,
            fullError: errorData,
            requestData: error.config?.data
          });
          return createApiError(
            'Server validation error. Please check your input format and try again. If the problem persists, contact support.',
            status,
            errorData
          );
        }
        
        return createApiError(
          isDevelopment
            ? errorData.message
            : 'An error occurred while processing your request.',
          status,
          errorData
        );
      }

      // Handle specific error cases
      switch (status) {
        case 400:
          return createApiError(
            isDevelopment
              ? `Bad Request: ${errorData?.message || error.message}`
              : 'Invalid request. Please check your input and try again.',
            status,
            errorData
          );
        case 401:
          // Check if this is a token validation error
          if (errorData?.message?.includes('token') || errorData?.message?.includes('expired') || errorData?.message?.includes('unauthorized')) {
            console.log('🔑 Token expired or invalid, clearing auth data');
            // Clear auth data but don't redirect immediately
            this.clearAuthData();
            return createApiError(
              'Your session has expired. Please log in again.',
              status,
              errorData
            );
          }
          return createApiError(
            isDevelopment
              ? `Unauthorized: ${errorData?.message || error.message}`
              : 'Authentication failed. Please check your credentials and try again.',
            status,
            errorData
          );
        case 403:
          return createApiError(
            isDevelopment
              ? `Forbidden: ${errorData?.message || error.message}`
              : 'You do not have permission to perform this action.',
            status,
            errorData
          );
        case 404:
          return createApiError(
            isDevelopment
              ? `Not Found: ${errorData?.message || error.message}`
              : 'The requested resource was not found.',
            status,
            errorData
          );
        case 422:
          // Handle Laravel validation errors
          if (errorData?.errors) {
            const validationErrors = Object.values(errorData.errors).flat();
            return createApiError(
              isDevelopment
                ? `Validation Error: ${validationErrors.join(', ')}`
                : 'Please check your input and try again.',
              status,
              errorData
            );
          }
          return createApiError(
            isDevelopment
              ? `Validation Error: ${errorData?.message || error.message}`
              : 'Please check your input and try again.',
            status,
            errorData
          );
        case 500:
          return createApiError(
            isDevelopment
              ? `Server Error: ${errorData?.message || error.message}`
              : 'An unexpected error occurred. Please try again later.',
            status,
            errorData
          );
        default:
          return createApiError(
            isDevelopment
              ? `Error: ${errorData?.message || error.message}`
              : 'An error occurred. Please try again.',
            status,
            errorData
          );
      }
    }

    return createApiError(
      isDevelopment
        ? `Error: ${error.message}`
        : 'An unexpected error occurred. Please try again.',
      (error as any).response?.status
    );
  }

  private async isOnline(): Promise<boolean> {
    return navigator.onLine;
  }

  private encodeBase64(str: string): string {
    return btoa(str);
  }

  private clearAuthData(): void {
    console.log('🧹 Clearing authentication data...');
    this.currentUser = null;
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_data');
    // Clear cached auth data
    cacheService.clearAuth().catch(console.error);
  }

  // Debug method to test different input formats
  async testLoginFormats(identifier: string, pin: string): Promise<void> {
    console.log('Testing different login formats...');
    
    const testCases = [
      { identifier: identifier, pin: pin, description: 'Original inputs' },
      { identifier: identifier.trim(), pin: pin.trim(), description: 'Trimmed inputs' },
      { identifier: identifier.replace(/[^\w@.-]/g, ''), pin: pin.replace(/[^\d]/g, ''), description: 'Sanitized inputs' },
      { identifier: identifier.toLowerCase(), pin: pin, description: 'Lowercase identifier' },
      { identifier: identifier.toUpperCase(), pin: pin, description: 'Uppercase identifier' }
    ];

    for (const testCase of testCases) {
      try {
        console.log(`Testing: ${testCase.description}`, testCase);
        const response = await this.api.post('/auth/login', {
          identifier: testCase.identifier,
          pin: this.encodeBase64(testCase.pin),
          method: 'pin'
        });
        console.log(`✅ Success with: ${testCase.description}`, response.data);
        break; // Stop on first success
      } catch (error) {
        console.log(`❌ Failed with: ${testCase.description}`, error);
      }
    }
  }

  // Authentication Methods
  async login(identifier: string, password: string): Promise<{ user: User; token: string }> {
    try {
      // Sanitize inputs to prevent backend regex issues
      const sanitizedIdentifier = identifier.trim().replace(/[^\w@.-]/g, '');
      const sanitizedPassword = password.trim();
      
      if (!sanitizedIdentifier || !sanitizedPassword) {
        throw new Error('Invalid identifier or password format');
      }
      
      // Debug logging
      console.log('🔍 LOGIN DEBUG - Credentials Method:', {
        originalIdentifier: identifier,
        sanitizedIdentifier: sanitizedIdentifier,
        passwordLength: sanitizedPassword.length,
        encodedPassword: this.encodeBase64(sanitizedPassword),
        timestamp: new Date().toISOString()
      });
      
      const response = await this.api.post('/auth/login', {
        identifier: sanitizedIdentifier,
        password: this.encodeBase64(sanitizedPassword),
        method: 'credentials'
      });

      const data = response.data;
      
      if (data.status === 'success') {
        const { user, token } = data.data;
        
        // Store auth data
        localStorage.setItem('auth_token', token);
        localStorage.setItem('user_data', JSON.stringify(user));
        await cacheService.cacheAuth(token, user);

        // Set current user
        this.currentUser = user;

        // Set up token refresh
        this.setupTokenRefresh();

        // Fetch and cache initial projects data
        try {
          const projects = await this.getProjects(true);
          await cacheService.cacheProjects(projects);
        } catch (error) {
          console.warn('Failed to cache initial projects:', error);
        }

        return { user, token };
      }
      throw new Error(data.message || 'Login failed');
    } catch (error) {
      console.error('Login error:', error);
      const axiosError = error as AxiosError;
      
      // Handle specific error cases for better user experience
      if (axiosError.response?.status === 401) {
        throw createApiError('Invalid email/username or password. Please check your credentials and try again.', 401);
      } else if (axiosError.response?.status === 422) {
        const errorData = axiosError.response.data as LaravelErrorResponse;
        if (errorData.errors?.identifier) {
          throw createApiError('Please enter a valid email address or username.', 422);
        } else if (errorData.errors?.password) {
          throw createApiError('Password is required.', 422);
        }
        throw createApiError(errorData.message || 'Invalid credentials provided.', 422);
      } else if (axiosError.response?.status === 404) {
        throw createApiError('User not found. Please check your email/username and try again.', 404);
      } else if (axiosError.response?.status === 429) {
        throw createApiError('Too many login attempts. Please wait a few minutes before trying again.', 429);
      }
      
      throw this.handleError(axiosError);
    }
  }

  async loginWithPin(identifier: string, pin: string): Promise<{ user: User; token: string }> {
    try {
      // Sanitize inputs to prevent backend regex issues
      const sanitizedIdentifier = identifier.trim();
      const sanitizedPin = pin.replace(/[^0-9]/g, ''); // Only digits for PIN
      
      if (!sanitizedIdentifier || !sanitizedPin) {
        throw new Error('Invalid identifier or PIN format');
      }
      
      // Additional validation
      if (sanitizedPin.length < 4 || sanitizedPin.length > 8) {
        throw new Error('PIN must be between 4-8 digits');
      }
      
      // Debug logging
      console.log('🔍 LOGIN DEBUG - PIN Method:', {
        originalIdentifier: identifier,
        sanitizedIdentifier: sanitizedIdentifier,
        originalPin: pin,
        sanitizedPin: sanitizedPin,
        encodedPin: this.encodeBase64(sanitizedPin),
        timestamp: new Date().toISOString()
      });
      
      // Additional debugging - log the exact request payload
      const requestPayload = {
        identifier: sanitizedIdentifier,
        pin: this.encodeBase64(sanitizedPin),
        method: 'pin'
      };
      console.log('📤 REQUEST PAYLOAD:', requestPayload);
      console.log('📤 REQUEST PAYLOAD (JSON):', JSON.stringify(requestPayload));
      
      let response;
      let attempt = 1;
      const maxAttempts = 3;
      
      while (attempt <= maxAttempts) {
        try {
          console.log(`🔄 Login attempt ${attempt}/${maxAttempts}`);
          
          let testIdentifier = sanitizedIdentifier;
          let testPin = sanitizedPin;
          
          // Try different sanitization levels
          if (attempt === 2) {
            // Second attempt: remove special characters but keep alphanumeric
            testIdentifier = sanitizedIdentifier.replace(/[^a-zA-Z0-9@.-]/g, ''); // Keep email chars
            testPin = sanitizedPin.replace(/[^0-9]/g, ''); // Only digits
            console.log('🔄 Attempt 2: Alphanumeric + email chars for identifier');
          } else if (attempt === 3) {
            // Third attempt: use original identifier as-is
            testIdentifier = identifier.trim();
            testPin = sanitizedPin;
            console.log('🔄 Attempt 3: Using original identifier as-is');
          }
          
          response = await this.api.post('/auth/login', {
            identifier: testIdentifier,
            pin: this.encodeBase64(testPin),
            method: 'pin'
          });
          
          console.log(`✅ Login successful on attempt ${attempt}`);
          break; // Success, exit the loop
          
        } catch (error: any) {
          console.log(`❌ Attempt ${attempt} failed:`, error?.response?.data?.message || error.message);
          
          if (attempt === maxAttempts) {
            // All attempts failed
            throw error;
          }
          
          attempt++;
        }
      }

      const data = response.data;
      
      if (data.status === 'success') {
        const { user, token } = data.data;
        
        // Store auth data
        localStorage.setItem('auth_token', token);
        localStorage.setItem('user_data', JSON.stringify(user));
        await cacheService.cacheAuth(token, user);

        // Set current user
        this.currentUser = user;

        // Set up token refresh
        this.setupTokenRefresh();

        // Fetch and cache initial projects data
        try {
          const projects = await this.getProjects(true);
          await cacheService.cacheProjects(projects);
        } catch (error) {
          console.warn('Failed to cache initial projects:', error);
        }

        return { user, token };
      }
      throw new Error(data.message || 'Login failed');
    } catch (error) {
      console.error('Login error:', error);
      const axiosError = error as AxiosError;
      
      // Handle specific error cases for better user experience
      if (axiosError.response?.status === 401) {
        throw createApiError('Invalid email/username or PIN. Please check your credentials and try again.', 401);
      } else if (axiosError.response?.status === 422) {
        const errorData = axiosError.response.data as LaravelErrorResponse;
        if (errorData.errors?.identifier) {
          throw createApiError('Please enter a valid email address or username.', 422);
        } else if (errorData.errors?.pin) {
          throw createApiError('PIN is required and must be 6 digits.', 422);
        }
        throw createApiError(errorData.message || 'Invalid credentials provided.', 422);
      } else if (axiosError.response?.status === 404) {
        throw createApiError('User not found. Please check your email/username and try again.', 404);
      } else if (axiosError.response?.status === 429) {
        throw createApiError('Too many login attempts. Please wait a few minutes before trying again.', 429);
      }
      
      throw this.handleError(axiosError);
    }
  }

  async validateToken(): Promise<boolean> {
    try {
      const response = await this.api.post('/auth/validate');
      return response.status === 200;
    } catch {
      return false;
    }
  }

  logout(): void {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_data');
    localStorage.removeItem('auth_timestamp');
    this.currentUser = null;
    cacheService.clearAuth();
  }

  // Project Methods - Cache First Approach
  async getProjects(forceSync: boolean = false): Promise<Project[]> {
    try {
      // Try to get cached projects first if not forcing sync
      if (!forceSync) {
        const cachedProjects = await cacheService.getCachedProjects();
        if (cachedProjects.length > 0) {
          return cachedProjects;
        }
      }

      // Fetch fresh data from Laravel API
      const response = await this.api.get('/projects');
      
      // Laravel API returns data in a specific format
      if (!response.data || !response.data.data) {
        throw new Error('Invalid response format from server');
      }

      const projects = response.data.data;
      if (!Array.isArray(projects)) {
        throw new Error('Invalid response format from server');
      }

      const transformedProjects: Project[] = projects.map((project: any) => ({
        id: project.id.toString(),
        code: project.code || project.id.toString(),
        name: project.name || project.code || project.id.toString(),
        so_number: project.so_number || '',
        customer_name: project.customer_name || '',
        customer_contact: project.customer_contact || '',
        order_id: project.order_id || '',
        order_date: project.order_date || '',
        start_date: project.start_date || '',
        end_date: project.end_date || '',
        status: this.mapProjectStatus(project.stage_id),
        description: project.description || '',
        project_type: project.project_type || '',
        project_city: project.project_city || '',
        partner_name: project.partner_name || '',
        delivery_address: project.delivery_address || '',
        created_at: project.created_at || new Date().toISOString(),
        updated_at: project.updated_at || new Date().toISOString(),
        statistics: project.statistics || {
          cartons: { total_cartons: 0 },
          panels: { total_panels: 0 },
          hardware: { total_hardware: 0 }
        }
      }));

      // Cache the transformed projects
      await cacheService.cacheProjects(transformedProjects);
      return transformedProjects;
    } catch (error) {
      console.error('Error fetching projects:', error);
      // If fetch fails and we have cached data, return that
      if (!forceSync) {
        const cachedProjects = await cacheService.getCachedProjects();
        if (cachedProjects.length > 0) {
          return cachedProjects;
        }
      }
      throw this.handleError(error as AxiosError);
    }
  }

  /**
   * Map backend stage ID to frontend status
   */
  private mapProjectStatus(stageId: number | null): string {
    if (!stageId) return 'planning';

    const statusMap: { [key: number]: string } = {
      17: 'planning',
      18: 'in_progress',
      19: 'completed',
      20: 'on_hold',
      21: 'cancelled'
    };

    return statusMap[stageId] || 'planning';
  }

  // Make filterProjects public
  public filterProjects(projects: Project[], filter?: ProjectFilter): Project[] {
    if (!filter) return projects;

    return projects.filter(project => {
      // Status filter
      if (filter.status.length > 0 && !filter.status.includes(project.status)) {
        return false;
      }

      // Search term filter
      if (filter.searchTerm) {
        const searchTerm = filter.searchTerm.toLowerCase();
        const matchesSearch = 
          project.code.toLowerCase().includes(searchTerm) ||
          project.order_id.toLowerCase().includes(searchTerm) ||
          project.customer_name.toLowerCase().includes(searchTerm);
        if (!matchesSearch) return false;
      }

      // Customer name filter
      if (filter.customerName && !project.customer_name.toLowerCase().includes(filter.customerName.toLowerCase())) {
        return false;
      }

      // Order ID filter
      if (filter.orderId && !project.order_id.toLowerCase().includes(filter.orderId.toLowerCase())) {
        return false;
      }

      // Project ID filter
      if (filter.projectId && !project.id.toLowerCase().includes(filter.projectId.toLowerCase())) {
        return false;
      }

      // Date range filter
      if (filter.startDate || filter.endDate) {
        const orderDate = new Date(project.order_date);
        if (filter.startDate && orderDate < new Date(filter.startDate)) {
          return false;
        }
        if (filter.endDate && orderDate > new Date(filter.endDate)) {
          return false;
        }
      }

      return true;
    });
  }

  async getProject(id: string): Promise<Project> {
    try {
      const response = await this.api.post(`/projects/${id}/detailed`);
      if (response.data.status === 'success') {
        const apiData = response.data.data;
        const projectData = apiData.project;
        
        // Transform the API response to match the expected Project interface
        return {
          project: {
            id: projectData.id,
            code: projectData.project_id || projectData.id,
            display_name: projectData.display_name || projectData.customer_name || projectData.order_id,
            customer_name: projectData.customer_name || '',
            so_number: projectData.order_id || ''
          },
          statistics: {
            cartons: {
              total_cartons: apiData.statistics?.cartons?.total_cartons || 0,
              received_cartons: apiData.statistics?.cartons?.received_cartons || 0,
              pending_cartons: (apiData.statistics?.cartons?.total_cartons || 0) - (apiData.statistics?.cartons?.received_cartons || 0)
            },
            panels: {
              total_panels: apiData.statistics?.panels?.total_panels || 0,
              received_panels: apiData.statistics?.panels?.received_panels || 0,
              pending_panels: (apiData.statistics?.panels?.total_panels || 0) - (apiData.statistics?.panels?.received_panels || 0)
            },
            hardware: {
              total_hardware: apiData.statistics?.hardware?.total_hardware || 0,
              received_hardware: apiData.statistics?.hardware?.received_hardware || 0,
              pending_hardware: (apiData.statistics?.hardware?.total_hardware || 0) - (apiData.statistics?.hardware?.received_hardware || 0)
            }
          },
          cartons: apiData.cartons || [],
          panels: apiData.panels?.items || [],
          hardware: apiData.hardwareLabels?.items || []
        };
      }
      throw new Error('Failed to fetch project');
    } catch (error) {
      console.error(`Error fetching project ${id}:`, error);
      
      // Check if we're offline and try to get cached data
      const isOffline = !navigator.onLine;
      if (isOffline) {
        console.log('Offline mode detected, trying to get cached project data');
        try {
          // Try to get cached project data
          const cachedProjects = await cacheService.getCachedProjects();
          const cachedProject = cachedProjects.find(p => p.id === id);
          
          if (cachedProject) {
            console.log('Using cached project data for offline mode');
            return cachedProject;
          }
          
          // If no cached project found, create a minimal project structure
          console.log('No cached project found, creating minimal project structure');
          return {
            project: {
              id: id,
              code: id,
              display_name: 'Project (Offline)',
              customer_name: 'Offline Mode',
              so_number: ''
            },
            statistics: {
              cartons: { total_cartons: 0, received_cartons: 0, pending_cartons: 0 },
              panels: { total_panels: 0, received_panels: 0, pending_panels: 0 },
              hardware: { total_hardware: 0, received_hardware: 0, pending_hardware: 0 }
            },
            cartons: [],
            panels: [],
            hardware: []
          };
        } catch (cacheError) {
          console.error('Error accessing cached data:', cacheError);
        }
      }
      
      throw this.handleError(error as AxiosError);
    }
  }

  async getProjectDetailed(id: string): Promise<Project> {
    try {
      console.log('Fetching detailed project with ID:', id);
      const response = await this.api.post(`/projects/${id}/detailed`);
      console.log('Raw API response:', response.data);
      
      if (response.data.status === 'success') {
        const projectData = response.data.data.project;
        const statistics = response.data.data.statistics;
        console.log('Project data from response:', projectData);
        console.log('Statistics from response:', statistics);
        
        if (!projectData) {
          throw new Error('No project data received from API');
        }

        // Use statistics from the separate statistics object in the response
        const transformedStatistics = {
          cartons: {
            total_cartons: statistics?.cartons?.total_cartons || 0,
            packed_cartons: statistics?.cartons?.packed_cartons || 0,
            dispatched_cartons: statistics?.cartons?.dispatched_cartons || 0,
            received_cartons: statistics?.cartons?.received_cartons || 0
          },
          panels: {
            total_panels: statistics?.panels?.total_panels || 0,
            packed_panels: statistics?.panels?.packed_panels || 0,
            dispatched_panels: statistics?.panels?.dispatched_panels || 0,
            received_panels: statistics?.panels?.received_panels || 0
          },
          hardware: {
            total_hardware: statistics?.hardware?.total_hardware || 0,
            packed_hardware: statistics?.hardware?.packed_hardware || 0,
            dispatched_hardware: statistics?.hardware?.dispatched_hardware || 0,
            received_hardware: statistics?.hardware?.received_hardware || 0
          }
        };

        const transformedProject = {
          id: projectData.id || '',
          code: projectData.project_id || projectData.id || '',
          name: projectData.display_name || projectData.customer_name || '',
          so_number: projectData.so_number || '',
          customer_name: projectData.customer_name || '',
          customer_contact: projectData.customer_contact || '',
          order_id: projectData.order_id || '',
          order_date: projectData.order_date || '',
          start_date: projectData.start_date || '',
          end_date: projectData.end_date || '',
          status: projectData.status || 'pending',
          description: projectData.description || '',
          project_type: projectData.project_type || '',
          project_city: projectData.project_city || '',
          partner_name: projectData.partner_name || '',
          delivery_address: projectData.delivery_address || '',
          created_at: projectData.created_at || new Date().toISOString(),
          updated_at: projectData.updated_at || new Date().toISOString(),
          statistics: transformedStatistics
        };
        
        console.log('Transformed project data:', transformedProject);
        return transformedProject;
      }
      throw new Error('Failed to fetch project details: Invalid response status');
    } catch (error) {
      console.error(`Error fetching detailed project ${id}:`, error);
      if (error instanceof Error) {
        throw new Error(`Failed to fetch project details: ${error.message}`);
      }
      throw error;
    }
  }

  // Carton Methods - Cache First Approach
  async getProjectCartons(projectId: string, filter?: CartonFilter): Promise<ProjectCarton[]> {
    try {
      const response = await this.api.get(`/projects/${projectId}/cartons`, {
        params: filter
      });
      
      console.log('getProjectCartons response:', response.data);
      
      // The backend returns { data: cartons[] } directly
      if (response.data && response.data.data) {
        const cartons = response.data.data as Array<Partial<ProjectCarton>>;
        // Transform and cache each carton individually
        const transformedCartons: ProjectCarton[] = cartons.map(carton => ({
          id: carton.id || '',
          userId: carton.userId || '',
          syncStatus: 'synced',
          project_id: projectId,
          code: carton.code || '',
          status: carton.status || 'pending',
          is_received: carton.is_received || false,
          received_at: carton.received_at,
          received_by: carton.received_by,
          remarks: carton.remarks,
          timestamp: Date.now()
        }));

        // Cache each transformed carton
        try {
          for (const carton of transformedCartons) {
            await cacheService.cacheCarton(carton);
          }
        } catch (cacheError) {
          console.warn('Failed to cache cartons, but continuing:', cacheError);
          // Don't fail the entire operation if caching fails
        }

        return transformedCartons;
      } else {
        console.error('API response error:', response.data);
        throw new Error(`API Error: ${response.data.message || 'Failed to fetch project cartons'}`);
      }
    } catch (error) {
      console.error('Error fetching project cartons:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw this.handleError(error as AxiosError);
    }
  }

  async getCarton(cartonId: string): Promise<ProjectCarton> {
    const response: AxiosResponse<ProjectCarton> = await this.api.get(`/cartons/${cartonId}`);
    return response.data;
  }

  async getCartonDetails(cartonId: string): Promise<{
    carton: ProjectCarton;
    panels: ProjectCartonPanel[];
    hardware: ProjectHardware[];
    panelCount: number;
    hardwareCount: number;
  }> {
    const response = await this.api.post(`/cartons/${cartonId}/details`);
    return response.data;
  }

  async getCartonReceivingStatus(cartonId: string): Promise<CartonReceivingStatus> {
    const response: AxiosResponse<CartonReceivingStatus> = await this.api.get(`/cartons/${cartonId}/receiving-status`);
    return response.data;
  }

  async scanCarton(cartonId: string, scannedCode: string): Promise<ProjectCarton> {
    console.log('[API] scanCarton called with:', { cartonId, scannedCode });
    console.log('[API] Online status:', navigator.onLine);
    
    if (!await this.isOnline()) {
      console.log('[API] Offline mode - adding carton scan to offline queue');
      
      // Import offline queue dynamically to avoid circular dependencies
      const { offlineQueue } = await import('./offlineQueue');
      
      // Add to offline queue
      await offlineQueue.addAction('SCAN_CARTON', {
        cartonId,
        scannedCode
      });
      
      console.log('[API] Carton scan action added to offline queue');
      
      // Offline mode - return null to indicate no data available
      return null;
    }
    
    console.log('[API] Online mode - making carton scan API call');
    const response: AxiosResponse<ProjectCarton> = await this.api.post(`/cartons/${cartonId}/scan`, {
      scanned_code: scannedCode
    });
    console.log('[API] Carton scan response:', response.data);
    return response.data;
  }

  async updateCartonStatus(cartonId: string, status: string, projectId: string, remarks?: string): Promise<ProjectCarton> {
    console.log('[API] updateCartonStatus called with:', { cartonId, status, projectId, remarks });
    console.log('[API] Online status:', navigator.onLine);
    
    if (!await this.isOnline()) {
      console.log('[API] Offline mode - adding carton status update to offline queue');
      
      // Import offline queue dynamically to avoid circular dependencies
      const { offlineQueue } = await import('./offlineQueue');
      
      // Add to offline queue
      const actionType = status === 'received' ? 'RECEIVE_CARTON' : 'UPDATE_CARTON_STATUS';
      await offlineQueue.addAction(actionType, {
        cartonId,
        status,
        projectId,
        remarks
      });
      
      console.log('[API] Carton status update action added to offline queue:', actionType);
      
      // Offline mode - return null to indicate no data available
      return null;
    }
    
    console.log('[API] Online mode - making carton status update API call');
    const response: AxiosResponse<ProjectCarton> = await this.api.put(`/cartons/${cartonId}/status`, {
      status,
      remarks,
      project_id: projectId
    });
    console.log('[API] Carton status update response:', response.data);
    return response.data;
  }

  // Carton Receiving Methods
  async receiveCarton(cartonId: string, data: { 
    received_by: number; 
    remarks?: string;
    project_id: string;
  }): Promise<ProjectCarton> {
    if (!await this.isOnline()) {
      throw new Error('Internet connection required for receiving cartons');
    }
    const response: AxiosResponse<ProjectCarton> = await this.api.post(`/cartons/${cartonId}/receive`, data);
    return response.data;
  }

  async receiveCartonPanel(cartonId: string, panelId: string, data: CartonPanelReceivingRequest): Promise<ProjectCartonPanel> {
    if (!await this.isOnline()) {
      throw new Error('Internet connection required for receiving panels');
    }
    const response: AxiosResponse<ProjectCartonPanel> = await this.api.post(`/cartons/${cartonId}/panels/${panelId}/receive`, data);
    return response.data;
  }

  async receiveCartonHardware(cartonId: string, hardwareId: string, data: CartonHardwareReceivingRequest): Promise<ProjectHardware> {
    if (!await this.isOnline()) {
      throw new Error('Internet connection required for receiving hardware');
    }
    const response: AxiosResponse<ProjectHardware> = await this.api.post(`/cartons/${cartonId}/hardware/${hardwareId}/receive`, data);
    return response.data;
  }

  // Project Statistics
  async getProjectCartonStats(projectId: string): Promise<{
    totalCartons: number;
    packedCartons: number;
    dispatchedCartons: number;
    receivedCartons: number;
  }> {
    try {
      const response = await this.api.get(`/projects/${projectId}/carton-stats`);
      if (response.data.status === 'success') {
        const stats = response.data.data;
        // Ensure all required fields are present with default values
        return {
          totalCartons: Number(stats.totalCartons) || 0,
          packedCartons: Number(stats.packedCartons) || 0,
          dispatchedCartons: Number(stats.dispatchedCartons) || 0,
          receivedCartons: Number(stats.receivedCartons) || 0
        };
      }
      // Return default values if the request fails
      return {
        totalCartons: 0,
        packedCartons: 0,
        dispatchedCartons: 0,
        receivedCartons: 0
      };
    } catch (error) {
      console.error(`Error fetching carton stats for project ${projectId}:`, error);
      // Return default values on error
      return {
        totalCartons: 0,
        packedCartons: 0,
        dispatchedCartons: 0,
        receivedCartons: 0
      };
    }
  }

  async updateProjectStats(projectId: string, stats: {
    totalCartons?: number;
    packedCartons?: number;
    dispatchedCartons?: number;
    receivedCartons?: number;
  }): Promise<void> {
    try {
      const response = await this.api.put(`/projects/${projectId}/stats`, stats);
      if (response.data.status !== 'success') {
        throw new Error('Failed to update project stats');
      }
    } catch (error) {
      console.error(`Error updating stats for project ${projectId}:`, error);
      throw this.handleError(error as AxiosError);
    }
  }

  // Dashboard Methods - Cache First Approach
  async getDashboardStats(): Promise<DashboardStats> {
    try {
      const response = await this.api.get('/dashboard/stats');
      if (response.data.status === 'success') {
        return response.data.data;
      }
      throw new Error('Failed to fetch dashboard stats');
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      throw this.handleError(error as AxiosError);
    }
  }

  // Add method to force sync projects
  async syncProjects(): Promise<void> {
    try {
      const projects = await this.getProjects(true);
      await cacheService.cacheProjects(projects);
    } catch (error) {
      console.error('Error syncing projects:', error);
      throw this.handleError(error as AxiosError);
    }
  }

  // User Methods
  async getCurrentUser(): Promise<User | null> {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        return null;
      }

      const response = await this.api.post(`${this.baseUrl}/auth/me`);
      
      if (response.data.status === 'success') {
        const user = response.data.data;
        localStorage.setItem('user_data', JSON.stringify(user));
        return user;
      }
      
      // If response format is invalid, clear auth data
      localStorage.removeItem('auth_token');
      localStorage.removeItem('userId');
      await cacheService.clearAllCache();
      return null;
    } catch (error) {
      console.error('Error fetching current user:', error);
      // If API call fails, try to use cached user
      const cachedUser = localStorage.getItem('user_data');
      if (cachedUser) {
        return JSON.parse(cachedUser);
      }
      return null;
    }
  }

  async updateUserProfile(data: Partial<User>): Promise<User> {
    if (!await this.isOnline()) {
      throw new Error('Internet connection required for updating user profile');
    }
    const response: AxiosResponse<User> = await this.api.put('/users/profile', data);
    return response.data;
  }

  // Search Methods
  async searchCartons(query: string): Promise<ProjectCarton[]> {
    const response: AxiosResponse<ProjectCarton[]> = await this.api.get('/search/cartons', {
      params: { q: query }
    });
    return response.data;
  }

  async searchProjects(query: string): Promise<Project[]> {
    const response: AxiosResponse<Project[]> = await this.api.get('/search/projects', {
      params: { q: query }
    });
    return response.data;
  }

  // Health Check
  async healthCheck(): Promise<boolean> {
    try {
      await this.api.get('/health');
      return true;
    } catch {
      return false;
    }
  }

  // Version Check
  async checkVersion(): Promise<{ version: string; timestamp: string }> {
    try {
      const response = await this.api.get('/version');
      return response.data;
    } catch (error) {
      console.error('Version check failed:', error);
      return {
        version: process.env.REACT_APP_VERSION || '1.0.0',
        timestamp: new Date().toISOString()
      };
    }
  }

  // Utility method to find carton by code
  async findCartonByCode(code: string, projectId: string): Promise<ProjectCarton | null> {
    try {
      const response = await this.api.get(`/cartons/code/${code}`, {
        params: { project_id: projectId }
      });
      return response.data.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw this.handleError(error);
    }
  }

  async scanBarcode(barcode: string, projectId: string): Promise<ProjectCarton> {
    try {
      const response = await this.api.post('/cartons/scan', { barcode, project_id: projectId });
      return response.data.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async receiveCartonContents(cartonId: string, contents: Array<{
    id: string;
    type: 'panel' | 'hardware';
    quantity?: number;
    remarks?: string;
  }>): Promise<ProjectCarton> {
    try {
      const response = await this.api.post(`/cartons/${cartonId}/receive-contents`, { contents });
      return response.data.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async receiveCartonContent(data: {
    cartonId: string;
    contentCode: string;
    projectId: string;
    isPanel?: boolean;
    isHardware?: boolean;
  }): Promise<{ data: any; success: boolean; error?: any }> {
    console.log('[API] receiveCartonContent called with:', data);
    console.log('[API] Online status:', navigator.onLine);
    
    // Check if online
    if (!await this.isOnline()) {
      console.log('[API] Offline mode - adding to offline queue');
      
      // Import offline queue dynamically to avoid circular dependencies
      const { offlineQueue } = await import('./offlineQueue');
      
      // Add to offline queue
      const actionType = data.isPanel ? 'MARK_PANEL_RECEIVED' : 'MARK_HARDWARE_RECEIVED';
      await offlineQueue.addAction(actionType, {
        cartonId: data.cartonId,
        contentCode: data.contentCode,
        projectId: data.projectId,
        isPanel: data.isPanel || false,
        isHardware: data.isHardware || false
      });
      
      console.log('[API] Action added to offline queue:', actionType);
      
      // Return success for offline mode (will be processed when online)
      return {
        data: { 
          message: 'Action queued for offline processing',
          queued: true,
          contentCode: data.contentCode,
          cartonId: data.cartonId
        },
        success: true
      };
    }
    
    console.log('[API] Online mode - making API call');
    try {
      const response = await this.api.post(`/cartons/${data.cartonId}/content`, {
        content_code: data.contentCode,
        project_id: data.projectId,
        is_panel: data.isPanel || false,
        is_hardware: data.isHardware || false
      });
      
      console.log('[API] API response:', response.data);
      
      return {
        data: response.data.data,
        success: response.data.status === 'success'
      };
    } catch (error: any) {
      console.error('[API] API call failed:', error);
      const errorResponse = error.response?.data;
      return {
        data: errorResponse?.data || null,
        success: false,
        error: {
          code: errorResponse?.code || 'UNKNOWN_ERROR',
          message: errorResponse?.message || error.message,
          needsConfirmation: errorResponse?.needsConfirmation || false,
          type: errorResponse?.type,
          content: errorResponse?.content,
          isPanel: errorResponse?.isPanel,
          isHardware: errorResponse?.isHardware
        }
      };
    }
  }

  async handleConfirmation(data: {
    cartonId: string;
    type: string;
    content: string;
    projectId: string;
    confirmed: boolean;
  }): Promise<{ data: any; success: boolean; error?: any }> {
    try {
      const response = await this.api.post(`/cartons/${data.cartonId}/confirm`, {
        type: data.type,
        content: data.content,
        project_id: data.projectId,
        confirmed: data.confirmed
      });
      
      return {
        data: response.data.data,
        success: response.data.status === 'success'
      };
    } catch (error: any) {
      const errorResponse = error.response?.data;
      return {
        data: null,
        success: false,
        error: {
          code: errorResponse?.code || 'UNKNOWN_ERROR',
          message: errorResponse?.message || error.message
        }
      };
    }
  }

  async validateScannedCode(cartonId: string, code: string, projectId: string): Promise<{ data: any; success: boolean; error?: any }> {
    try {
      const response = await this.api.post(`/cartons/${cartonId}/validate`, {
        code: code,
        project_id: projectId
      });
      
      return {
        data: response.data.data,
        success: response.data.status === 'success'
      };
    } catch (error: any) {
      const errorResponse = error.response?.data;
      return {
        data: null,
        success: false,
        error: {
          code: errorResponse?.code || 'UNKNOWN_ERROR',
          message: errorResponse?.message || error.message
        }
      };
    }
  }

  async getPendingItems(cartonId: string): Promise<{ data: any; success: boolean; error?: any }> {
    try {
      const response = await this.api.get(`/cartons/${cartonId}/pending-items`);
      
      return {
        data: response.data.data,
        success: response.data.status === 'success'
      };
    } catch (error: any) {
      const errorResponse = error.response?.data;
      return {
        data: null,
        success: false,
        error: {
          code: errorResponse?.code || 'UNKNOWN_ERROR',
          message: errorResponse?.message || error.message
        }
      };
    }
  }

  async checkAuth(): Promise<User> {
    try {
      // First try to get user from localStorage
      const cachedUser = localStorage.getItem('user_data');
      if (cachedUser) {
        return JSON.parse(cachedUser);
      }

      // If no cached user, try to get from API
      const response = await this.api.post('/auth/me');
      if (response.data.success === true) {
        const user = response.data.data.user;
        localStorage.setItem('user_data', JSON.stringify(user));
        return user;
      }
      throw new Error('Failed to get user data');
    } catch (error) {
      // If API call fails, try to use cached user
      const cachedUser = localStorage.getItem('user_data');
      if (cachedUser) {
        return JSON.parse(cachedUser);
      }
      throw error;
    }
  }

  // Report Methods
  async getReports(): Promise<Report[]> {
    try {
      const response = await this.api.get('/reports');
      return response.data.data;
    } catch (error) {
      throw this.handleError(error as AxiosError);
    }
  }

  async downloadReport(reportId: string): Promise<void> {
    try {
      const response = await this.api.get(`/reports/${reportId}/download`, {
        responseType: 'blob'
      });
      
      // Create a blob from the response data
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      
      // Create a URL for the blob
      const url = window.URL.createObjectURL(blob);
      
      // Create a temporary link element
      const link = document.createElement('a');
      link.href = url;
      link.download = `report-${reportId}.pdf`; // or get filename from headers
      
      // Append link to body, click it, and remove it
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the URL
      window.URL.revokeObjectURL(url);
    } catch (error) {
      throw this.handleError(error as AxiosError);
    }
  }

  async validateToken(): Promise<boolean> {
    try {
      const response = await this.api.post('/auth/validate');
      
      // The backend returns: { success: true, data: { user: {...} } }
      const isValid = response.data.success === true && response.data.data?.user;
      
      if (!isValid) {
        console.log('🔑 Token validation failed, clearing auth data');
        this.clearAuthData();
      } else {
        console.log('🔑 Token validation successful');
      }
      
      return isValid;
    } catch (error: any) {
      console.log('🔑 Token validation error:', error);
      
      // If it's a 401, the token is definitely invalid
      if (error?.response?.status === 401) {
        console.log('🔑 Token validation returned 401, clearing auth data');
        this.clearAuthData();
      }
      
      return false;
    }
  }

  isLoggedIn(): boolean {
    const hasToken = !!localStorage.getItem('auth_token');
    const hasUser = !!this.currentUser;
    
    if (!hasToken || !hasUser) {
      console.log('🔍 User not logged in - missing token or user data');
      return false;
    }
    
    return true;
  }
}

// Export a singleton instance
export const apiService = new ApiService();
export { apiService as api };
export default apiService; 